// A topic's page: its name and description, the issues on its list (edited directly) when it
// has any, its examples, and its files. "Review" opens the review screen. The conversation sits
// beside it. `/topics/:key` and `/topics/:key/issues` are the same page.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { type ApiClient } from '../api/client.js';
import type { FileView, TopicView } from '../api/contract.js';
import { topicListDescription, topicPhrase } from '../format.js';
import type { Internals } from '../internals.js';
import {
  type PlanSnapshot,
  viewerIsPrincipal,
  viewerLacksSay,
} from '../plan/snapshot.js';
import type { PlanState } from '../plan/usePlan.js';
import { Decisions } from '../surfaces/Decisions.js';
import { AddFile } from './AddFile.js';
import { FileLine } from './FileRows.js';

export type TopicListPageProps = {
  state: PlanState;
  internals: Internals;
  client: ApiClient;
  planId: string;
  topic: TopicView;
  /** The conversation on screen, which an added file records as where it came from. */
  originThreadId: string | null;
};

export function TopicListPage({
  state,
  internals,
  client,
  planId,
  topic,
  originThreadId,
}: TopicListPageProps): JSX.Element {
  const navigate = useNavigate();
  const snapshot = state.snapshot;
  const [files, setFiles] = useState<FileView[]>([]);
  const [filesVersion, setFilesVersion] = useState(0);
  const reloadFiles = useCallback(() => setFilesVersion((value) => value + 1), []);
  useEffect(() => {
    let current = true;
    client.listTopicFiles(planId, topic.key).then(
      (loaded) => {
        if (current) {
          setFiles(loaded);
        }
      },
      () => undefined,
    );
    return () => {
      current = false;
    };
  }, [client, planId, topic.key, snapshot, filesVersion]);
  const noSay = snapshot !== null && viewerLacksSay(snapshot);
  const hasIssues =
    snapshot?.decisions.some((issue) => issue.topic === topic.key) ?? false;
  const objective =
    snapshot?.objectives.find((item) => item.topic === topic.key && item.text !== null)
      ?.text ?? null;

  return (
    <section className="page topicPage">
      <div>
        <div className="home__eyebrow">Planning topic</div>
        <h2 className="page__title">{topicPhrase(topic)}</h2>
      </div>
      <p className="page__subtitle">{topicListDescription(topic)}</p>
      <hr className="topicPage__rule" />
      {objective === null ? null : (
        <>
          <h3 className="home__eyebrow topicPage__eyebrow">What this is for</h3>
          <p>{objective}</p>
        </>
      )}
      {hasIssues ? (
        <>
          <h3 className="home__eyebrow topicPage__eyebrow">Your issues</h3>
          <Decisions
            state={state}
            internals={internals}
            client={client}
            planId={planId}
            topic={topic}
            addLabel="Add an issue"
          />
        </>
      ) : (
        <>
          <p className="topicPage__empty">Nothing is recorded yet.</p>
          <p className="topicPage__emptyNote">
            What you tell Mor about your own situation will appear here, and you can check
            or correct each part.
          </p>
        </>
      )}
      {snapshot === null ? null : (
        <>
          <h3 className="home__eyebrow topicPage__eyebrow">Files</h3>
          {files.length === 0 ? null : (
            <ul className="people__list">
              {files.map((file) => (
                <FileLine
                  key={file.id}
                  variant="card"
                  client={client}
                  planId={planId}
                  file={file}
                  people={snapshot.people}
                  viewerIsPrincipal={viewerIsPrincipal(snapshot)}
                  onChanged={reloadFiles}
                />
              ))}
            </ul>
          )}
          <AddFile
            asRow
            client={client}
            planId={planId}
            people={snapshot.people}
            viewerIsPrincipal={viewerIsPrincipal(snapshot)}
            originThreadId={originThreadId}
            note={
              <>
                The file belongs to the whole plan and appears in{' '}
                <Link to={`/plans/${planId}/files`}>Files</Link>.
              </>
            }
            onAdded={reloadFiles}
          />
        </>
      )}
      <div className="topicPage__examples">
        <div className="home__eyebrow">For example</div>
        <p className="topicPage__examplesText">{topic.examples.join(' · ')}</p>
      </div>
      <div className="page__foot">
        {noSay && topic.viewer.finished ? null : (
          <button
            type="button"
            className="button button--primary"
            onClick={() => {
              client.reviewTopic(planId, topic.key).catch(() => undefined);
              navigate(`/plans/${planId}/topics/${topic.key}/review`);
            }}
          >
            Review
          </button>
        )}
      </div>
    </section>
  );
}
